import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, ArrowUpRight, ArrowDownLeft } from "lucide-react";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription } from "@/components/ui/drawer.js";
import { Button } from "@/components/ui/button.js";
import { useAuth } from "@/auth/useAuth.js";
import {
  KEY_LOCATIONS, KEY_LOCATION_LABEL,
  useListingKeys, useSaveKeys, useAddKeyEvent,
  type KeyLocation,
} from "@/hooks/useKeys.js";

const fmtWhen = (iso: string) =>
  new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" }) +
  " " + new Date(iso).toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" });

export function KeysDrawer({ listingId, listingLabel, onClose }: {
  listingId: string | null;
  listingLabel?: string;
  onClose: () => void;
}): JSX.Element {
  const { role } = useAuth();
  const canEdit = role === "owner" || role === "admin" || role === "editor";
  const { data, isLoading } = useListingKeys(listingId);
  const save = useSaveKeys();
  const addEvent = useAddKeyEvent();

  const [location, setLocation] = useState<KeyLocation>("ufficio");
  const [lockboxCode, setLockboxCode] = useState("");
  const [holder, setHolder] = useState("");
  const [person, setPerson] = useState("");

  // Reset immediato quando si apre per un altro immobile: mai mostrare (o
  // salvare) i dati del precedente mentre arriva la fetch.
  useEffect(() => {
    setLocation("ufficio");
    setLockboxCode("");
    setHolder("");
    setPerson("");
  }, [listingId]);

  // Sincronizza dal server solo quando la riga di stato cambia davvero
  // (updated_at), non a ogni refetch: niente wipe degli input a meta' modifica.
  const keysStamp = data ? (data.keys?.updated_at ?? "none") : null;
  useEffect(() => {
    if (!data) return;
    setLocation(data.keys?.location ?? "ufficio");
    setLockboxCode(data.keys?.lockbox_code ?? "");
    setHolder(data.keys?.holder ?? "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listingId, keysStamp]);

  const saveState = (next: Partial<{ location: KeyLocation; lockbox_code: string; holder: string }>) => {
    if (!listingId || !canEdit) return;
    save.mutate({
      listingId,
      location: next.location ?? location,
      lockbox_code: (next.lockbox_code ?? lockboxCode) || null,
      holder: (next.holder ?? holder) || null,
    }, {
      onSuccess: () => toast.success("Registro chiavi aggiornato"),
      onError: (e) => toast.error(e instanceof Error ? e.message : "Salvataggio non riuscito"),
    });
  };

  const logEvent = (action: "presa" | "restituita") => {
    if (!listingId || person.trim().length < 2) {
      toast.error("Indica chi ha preso o restituito le chiavi");
      return;
    }
    addEvent.mutate({ listingId, action, person: person.trim() }, {
      onSuccess: () => {
        toast.success(action === "presa" ? "Presa registrata" : "Restituzione registrata");
        setPerson("");
      },
      onError: (e) => toast.error(e instanceof Error ? e.message : "Registrazione non riuscita"),
    });
  };

  return (
    <Drawer open={!!listingId} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DrawerContent className="flex flex-col">
        <div className="border-b border-rule p-5">
          <DrawerTitle className="flex items-center gap-2 text-[15px] font-semibold text-ink">
            <KeyRound className="size-4 text-accent" /> Registro chiavi
            {data?.out ? <span className="inline-flex h-[22px] items-center rounded-full border border-warn/50 bg-warn/10 px-[9px] font-mono text-[10.5px] uppercase text-warn">fuori</span> : null}
          </DrawerTitle>
          <DrawerDescription className="mt-1 truncate text-[12.5px] text-ink-soft">
            {listingLabel ?? "Immobile"}
          </DrawerDescription>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto p-5">
          {isLoading ? <p className="text-[13px] text-ink-mute">Carico…</p> : (
            <>
              <section>
                <p className="micro mb-2">Dove sono</p>
                <div className="flex flex-wrap gap-[6px]">
                  {KEY_LOCATIONS.map((loc) => (
                    <button
                      key={loc}
                      type="button"
                      disabled={!canEdit}
                      onClick={() => { setLocation(loc); saveState({ location: loc }); }}
                      className={`h-[26px] rounded-full border px-[11px] text-[12px] transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${location === loc ? "border-accent bg-accent/10 text-accent" : "border-rule bg-surface-2 text-ink-soft hover:border-ink-mute hover:text-ink"}`}
                    >
                      {KEY_LOCATION_LABEL[loc]}
                    </button>
                  ))}
                </div>
                {location === "cassetta" ? (
                  <label className="mt-3 flex items-center gap-[8px] text-[12px] text-ink-soft">
                    Codice cassetta
                    <input
                      value={lockboxCode}
                      disabled={!canEdit}
                      onChange={(e) => setLockboxCode(e.target.value)}
                      onBlur={() => saveState({})}
                      placeholder="es. 4812"
                      className="h-[28px] w-[110px] rounded-[5px] border border-rule bg-surface-2 px-2 font-mono text-[12px] text-ink outline-none focus:border-accent"
                    />
                  </label>
                ) : null}
                {location === "collega" || location === "proprietario" ? (
                  <label className="mt-3 flex items-center gap-[8px] text-[12px] text-ink-soft">
                    Chi le tiene
                    <input
                      value={holder}
                      disabled={!canEdit}
                      onChange={(e) => setHolder(e.target.value)}
                      onBlur={() => saveState({})}
                      placeholder="Nome"
                      className="h-[28px] w-[180px] rounded-[5px] border border-rule bg-surface-2 px-2 text-[12px] text-ink outline-none focus:border-accent"
                    />
                  </label>
                ) : null}
              </section>

              {canEdit ? (
                <section>
                  <p className="micro mb-2">Registra movimento</p>
                  <div className="flex flex-wrap items-center gap-[8px]">
                    <input
                      value={person}
                      onChange={(e) => setPerson(e.target.value)}
                      placeholder="Chi (es. Marco)"
                      aria-label="Persona"
                      className="h-[30px] w-[160px] rounded-[5px] border border-rule bg-surface-2 px-2 text-[12.5px] text-ink outline-none focus:border-accent"
                    />
                    <Button variant="secondary" size="sm" disabled={addEvent.isPending} onClick={() => logEvent("presa")}>
                      <ArrowUpRight className="size-[13px]" /> Presa
                    </Button>
                    <Button variant="secondary" size="sm" disabled={addEvent.isPending} onClick={() => logEvent("restituita")}>
                      <ArrowDownLeft className="size-[13px]" /> Restituita
                    </Button>
                  </div>
                </section>
              ) : null}

              <section>
                <p className="micro mb-2">Movimenti</p>
                {(data?.events ?? []).length === 0 ? (
                  <p className="text-[12.5px] text-ink-mute">Nessun movimento registrato.</p>
                ) : (
                  <div>
                    {(data?.events ?? []).map((ev) => (
                      <div key={ev.id} className="flex items-center gap-[10px] border-b border-rule-soft py-[9px] text-[12.5px] last:border-b-0">
                        {ev.action === "presa"
                          ? <ArrowUpRight className="size-[14px] shrink-0 text-warn" />
                          : <ArrowDownLeft className="size-[14px] shrink-0 text-success" />}
                        <span className="text-ink">
                          {ev.action === "presa" ? "Prese da" : "Restituite da"} <b className="font-semibold">{ev.person}</b>
                        </span>
                        <span className="ml-auto whitespace-nowrap font-mono text-[11px] text-ink-mute">{fmtWhen(ev.happened_at)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </>
          )}
        </div>
      </DrawerContent>
    </Drawer>
  );
}
